import { useEffect, useState, type RefObject } from 'react';
import {
  useForm,
  useFormContext,
  useWatch,
  type UseFormReturn,
} from 'react-hook-form';
import {
  useResetPassword,
  useSendRecoveryCode,
  useVerifyCode,
} from '~/domain/ForgotPassword';
import { useAuthFormSubmit } from '../../shared';

export const OTP_LENGTH = 5;
export const MIN_PASSWORD_LENGTH = 8;

export const EMPTY_FIELDS_MESSAGE = 'It looks like a few required fields were left empty.';
export const MIN_LENGTH_MESSAGE = 'All passwords must be at least 8 characters.';
export const MISMATCH_MESSAGE = 'Passwords do not match. Please try again.';

export interface IResetPasswordStepValues {
  newPassword: string;
  confirmPassword: string;
}

export interface IResetPasswordFormCallbacks {
  identifier: string;
  onResetSuccess: () => void;
}

export interface IBannerSpec {
  key: string;
  message: string;
  prefix?: string;
}

export const useResetPasswordForm = (): {
  methods: UseFormReturn<IResetPasswordStepValues>;
} => {
  const methods = useForm<IResetPasswordStepValues>({
    defaultValues: { newPassword: '', confirmPassword: '' },
    mode: 'onSubmit',
  });
  return { methods };
};

export interface IUseResetPasswordFormLogicResult {
  submit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  bannerRef: RefObject<HTMLDivElement | null>;
  banners: IBannerSpec[];
  showBannerArea: boolean;
  otp: string;
  otpError: boolean;
  newPwdError: boolean;
  confirmPwdError: boolean;
  isResetting: boolean;
  isResending: boolean;
  handleOtpChange: (value: string) => void;
  handleResend: () => void;
}

export const useResetPasswordFormLogic = ({
  identifier,
  onResetSuccess,
}: IResetPasswordFormCallbacks): IUseResetPasswordFormLogicResult => {
  const { getValues, control } = useFormContext<IResetPasswordStepValues>();

  const [otp, setOtp] = useState('');
  const [localValidationErrors, setLocalValidationErrors] = useState<string[]>([]);

  const watchedNewPassword = useWatch({ control, name: 'newPassword' });
  const watchedConfirmPassword = useWatch({ control, name: 'confirmPassword' });

  // Typing in any of the three validated fields resets the red borders + banner.
  useEffect(() => {
    if (localValidationErrors.length > 0) {
      setLocalValidationErrors([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedNewPassword, watchedConfirmPassword, otp]);

  const verifyMutation = useVerifyCode();
  const sendRecoveryMutation = useSendRecoveryCode();
  const resetPasswordMutation = useResetPassword();

  useEffect(() => {
    if (resetPasswordMutation.isSuccess) {
      onResetSuccess();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetPasswordMutation.isSuccess]);

  const { submit, bannerRef } = useAuthFormSubmit<IResetPasswordStepValues>({
    onValid: () => {
      const { newPassword, confirmPassword } = getValues();
      runSubmit(newPassword, confirmPassword);
    },
    onInvalid: () => {
      const { newPassword, confirmPassword } = getValues();
      runSubmit(newPassword, confirmPassword);
    },
  });

  const runSubmit = async (newPassword: string, confirmPassword: string) => {
    const errors: string[] = [];

    const newEmpty = !newPassword;
    const confirmEmpty = !confirmPassword;
    const otpIncomplete = otp.length < OTP_LENGTH;
    const anyEmpty = newEmpty || confirmEmpty || otpIncomplete;

    if (anyEmpty) errors.push(EMPTY_FIELDS_MESSAGE);

    const newTooShort = !newEmpty && newPassword.length < MIN_PASSWORD_LENGTH;
    const confirmTooShort = !confirmEmpty && confirmPassword.length < MIN_PASSWORD_LENGTH;
    if (newTooShort || confirmTooShort) errors.push(MIN_LENGTH_MESSAGE);

    if (!newEmpty && !confirmEmpty && newPassword !== confirmPassword) {
      errors.push(MISMATCH_MESSAGE);
    }

    if (errors.length > 0) {
      setLocalValidationErrors(errors);
      bannerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    setLocalValidationErrors([]);

    try {
      const { requestToken } = await verifyMutation.mutateAsync({
        identifier,
        otpCode: otp,
      });
      await resetPasswordMutation.mutateAsync({ identifier, requestToken, newPassword });
    } catch {
      // BE errors surface via verifyMutation.error / resetPasswordMutation.error
      bannerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleOtpChange = (value: string) => {
    setOtp(value);
    if (verifyMutation.isError) verifyMutation.reset();
  };

  const handleResend = () => {
    if (sendRecoveryMutation.isPending) return;
    sendRecoveryMutation.mutate({ identifier });
  };

  useEffect(() => {
    if (sendRecoveryMutation.isSuccess) {
      setOtp('');
      verifyMutation.reset();
      sendRecoveryMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendRecoveryMutation.isSuccess]);

  const beVerifyError = verifyMutation.isError
    ? verifyMutation.error instanceof Error
      ? verifyMutation.error.message
      : 'Something went wrong. Please try again.'
    : undefined;
  const beResetError = resetPasswordMutation.isError
    ? resetPasswordMutation.error instanceof Error
      ? resetPasswordMutation.error.message
      : 'Something went wrong. Please try again.'
    : undefined;

  // BE errors replace local validation banners.
  const banners: IBannerSpec[] = [];
  if (beResetError) {
    banners.push({ key: 'be-reset', message: beResetError });
  } else if (beVerifyError) {
    banners.push({ key: 'be-verify', message: beVerifyError });
  } else {
    localValidationErrors.forEach((message) => {
      if (message === EMPTY_FIELDS_MESSAGE) {
        banners.push({ key: 'empty', message });
      } else {
        // Secondary banners render without the "Oops!" prefix.
        banners.push({ key: message, message, prefix: '' });
      }
    });
  }

  const emptyFieldsFlagged = localValidationErrors.includes(EMPTY_FIELDS_MESSAGE);
  const minLengthFlagged = localValidationErrors.includes(MIN_LENGTH_MESSAGE);
  const mismatchFlagged = localValidationErrors.includes(MISMATCH_MESSAGE);

  const newPwdError =
    (emptyFieldsFlagged && !getValues('newPassword')) || minLengthFlagged || mismatchFlagged;
  const confirmPwdError =
    (emptyFieldsFlagged && !getValues('confirmPassword')) || minLengthFlagged || mismatchFlagged;
  const otpError =
    verifyMutation.isError || (emptyFieldsFlagged && otp.length < OTP_LENGTH);

  return {
    submit,
    bannerRef,
    banners,
    showBannerArea: banners.length > 0,
    otp,
    otpError,
    newPwdError,
    confirmPwdError,
    isResetting: resetPasswordMutation.isPending || verifyMutation.isPending,
    isResending: sendRecoveryMutation.isPending,
    handleOtpChange,
    handleResend,
  };
};
